import {
    CognitoUserPool,
    CognitoUser,
    CognitoUserAttribute,
    AuthenticationDetails,
} from 'amazon-cognito-identity-js'

export default class SmoAuth {
    constructor() {
        this.userPool = new CognitoUserPool({
            UserPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
            ClientId: process.env.REACT_APP_AWS_USER_POOL_CLIENT_ID,
        })

        this.user = null
    }

    createCognitoUser = (username) => {
        return new CognitoUser({
            Username: username,
            Pool: this.userPool,
        })
    }

    signUp = (username, password, email, attributes = {}) =>
        new Promise((resolve, reject) => {
            if (!username) throw Error("'username' is a mandatory field")
            if (!password) throw Error("'password' is a mandatory field")
            if (!email) throw Error("'email' is a mandatory field")

            const standardAttributes = [
                'address',
                'birthdate',
                'email',
                'family_name',
                'gender',
                'given_name',
                'locale',
                'middle_name',
                'name',
                'nickname',
                'phone_number',
                'picture',
                'preferred_username',
                'profile',
                'zoneinfo',
                'updated_at',
                'website',
            ]

            // email must be stored as an attribute
            attributes.email = email
            let attr = Object.entries(attributes).map(
                ([k, v]) =>
                    new CognitoUserAttribute({
                        Name: standardAttributes.includes(k)
                            ? k
                            : 'custom:' + k,
                        Value: v,
                    })
            )

            this.userPool.signUp(
                username,
                password,
                attr,
                null /*validationData*/,
                (err, result) => {
                    if (err) return reject(err)
                    else this.user = result.user
                    return resolve(result)
                }
            )
        })

    verify = (username, code) =>
        new Promise((resolve, reject) => {
            this.createCognitoUser(username).confirmRegistration(
                code,
                true,
                (err, result) => {
                    return err ? reject(err) : resolve(result)
                }
            )
        })

    resendCode = (email) =>
        new Promise((resolve, reject) => {
            this.createCognitoUser(email).resendConfirmationCode(
                (err, result) => {
                    return err ? reject(err) : resolve(result)
                }
            )
        })

    signIn = (email, password) =>
        new Promise((resolve, reject) => {
            var authenticationDetails = new AuthenticationDetails({
                Username: email,
                Password: password,
            })

            this.createCognitoUser(email).authenticateUser(
                authenticationDetails,
                {
                    onSuccess: function (result) {
                        this.user = result.user
                        return resolve({ status: 'success', result: result })
                    },
                    onFailure: (err) => {
                        return reject(err)
                    },
                    mfaRequired: (codeDeliveryDetails) => {
                        return resolve({
                            status: 'mfaRequired',
                            codeDeliveryDetails: codeDeliveryDetails,
                        })
                        //        cognitoUser.sendMFACode(mfaCode, this);
                    },

                    newPasswordRequired: (
                        userAttributes,
                        requiredAttributes
                    ) => {
                        // the api doesn't accept this field back
                        delete userAttributes.email_verified

                        // store userAttributes on global variable
                        // sessionUserAttributes = userAttributes;
                        return resolve({
                            status: 'newPasswordRequired',
                            userAttributes: userAttributes,
                            requiredAttributes: requiredAttributes,
                        })
                    },
                }
            )
        })

    signout = () =>
        new Promise((resolve, reject) => {
            const usr = this.userPool.getCurrentUser()
            if (usr) usr.signOut()
            return resolve() // SignOut never returns so I'm just assuming the sign out worked...
        })

    getToken = () =>
        new Promise((resolve, reject) => {
            var cognitoUser = this.userPool.getCurrentUser()

            if (!cognitoUser) resolve(null)

            cognitoUser.getSession((err, session) => {
                if (err) reject(err)
                else if (!session.isValid()) resolve(null)
                else resolve(session.getIdToken().getJwtToken())
            })
        })

    isSessionValid = () => {
        return this.userPool
            .getCurrentUser()
            ?.getSession((err, sess) => !err && sess.isValid())
    }

    verifyAndSignInAndGetToken = (email, password, code) =>
        new Promise((resolve, reject) => {
            this.verify(email, code)
                .then(() => {
                    this.signIn(email, password).then(() => {
                        return resolve(this.getToken())
                    })
                })
                .catch((err) => {
                    return reject(err)
                })
        })

    signInAndGetToken = (email, password) =>
        new Promise((resolve, reject) => {
            this.signIn(email, password)
                .then(() => {
                    return resolve(this.getToken())
                })
                .catch((err) => {
                    return reject(err)
                })
        })

    resetPasswordRequest = (email) => {
        return new Promise((resolve, reject) => {
            const cognitoUser = this.createCognitoUser(email)
            cognitoUser.forgotPassword({
                onSuccess: function (result) {
                    return resolve(result)
                },
                onFailure: function (err) {
                    return reject(err)
                },
            })
        })
    }

    resetPassword = (email, verificationCode, newPassword) => {
        return new Promise((resolve, reject) => {
            const cognitoUser = this.createCognitoUser(email)

            cognitoUser.confirmPassword(verificationCode, newPassword, {
                onSuccess: function () {
                    return resolve()
                },
                onFailure: function (err) {
                    return reject(err)
                },
            })
        })
    }
}
